// Pagar pedido one-shot (tienda o kit suelto) con Payment Brick MP.
// Acepta TODAS las opciones de pago: TC, TD, OCA, Abitab, RedPagos, MP wallet, transferencia.

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import Layout from '../../components/Layout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { cargarMP } from '../../lib/mp'

export default function PagarPedido() {
  const { profile, user } = useAuth()
  const navigate = useNavigate()
  const { pedidoId } = useParams()

  const [pedido, setPedido] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [exito, setExito] = useState(null)
  const [brickReady, setBrickReady] = useState(false)
  const brickControllerRef = useRef(null)

  useEffect(() => {
    let cancel = false
    async function load() {
      if (!pedidoId) { setError('Falta pedido'); setLoading(false); return }
      try {
        const { data, error: pe } = await supabase
          .from('pedidos')
          .select('id, numero_orden, monto_total, estado, kit_codigo, items, fecha_entrega_programada')
          .eq('id', pedidoId)
          .single()
        if (pe) throw pe
        if (cancel) return
        setPedido(data)
        if (data.estado === 'pagado') {
          setExito({ status: 'approved' })
        }
      } catch (e) {
        if (!cancel) setError(e.message)
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    load()
    return () => { cancel = true }
  }, [pedidoId])

  // Montar Payment Brick
  useEffect(() => {
    let cancel = false
    if (!pedido?.id || pedido.estado === 'pagado' || exito || brickReady) return
    ;(async () => {
      try {
        const mp = await cargarMP()
        if (cancel) return
        if (brickControllerRef.current) {
          brickControllerRef.current.unmount?.()
          brickControllerRef.current = null
        }
        const bricksBuilder = mp.bricks()
        const settings = {
          initialization: {
            amount: Number(pedido.monto_total),
            payer: { email: user?.email || profile?.email || '' },
          },
          customization: {
            paymentMethods: {
              creditCard: 'all',
              debitCard: 'all',
              ticket: 'all',
              bankTransfer: 'all',
              mercadoPago: 'all',
              maxInstallments: 1,
              minInstallments: 1,
            },
            visual: {
              style: {
                theme: 'default',
                customVariables: {
                  baseColor: '#1a3461',
                  baseColorSecondVariant: '#00c4d4',
                  borderRadiusFull: '10px',
                },
              },
            },
          },
          callbacks: {
            onReady: () => {
              setBrickReady(true)
              const ocultarBadge = () => {
                const root = document.getElementById('mp-pago-pedido-brick')
                if (!root) return
                root.querySelectorAll('*').forEach(el => {
                  if (el.children.length === 0) {
                    const txt = (el.textContent || '').trim().toLowerCase()
                    if (txt === 'cuotas disponibles' || txt === 'installments available') {
                      el.style.display = 'none'
                      if (el.parentElement) el.parentElement.style.display = 'none'
                    }
                  }
                })
              }
              ocultarBadge()
              setTimeout(ocultarBadge, 200)
              setTimeout(ocultarBadge, 600)
              setTimeout(ocultarBadge, 1500)
              const obs = new MutationObserver(ocultarBadge)
              obs.observe(root, { childList: true, subtree: true })
            },
            onSubmit: async ({ formData }) => {
              try {
                setError(null); setSubmitting(true)
                const { data, error } = await supabase.functions.invoke('mp-process-payment', {
                  body: {
                    pedido_id: pedido.id,
                    formData,
                    monto: Number(pedido.monto_total),
                    descripcion: `Limpack - Pedido ${pedido.numero_orden}`,
                  },
                })
                if (error) throw error
                if (data?.error) throw new Error(data.error + (data.detalle ? ': ' + JSON.stringify(data.detalle) : ''))

                if (data?.status === 'approved') {
                  setExito({ status: 'approved', payment_method: data.payment_method })
                  return Promise.resolve()
                }
                if (data?.status === 'pending' || data?.status === 'in_process') {
                  setExito({ status: 'pending', payment_method: data.payment_method, ticket_url: data.ticket_url })
                  return Promise.resolve()
                }
                return Promise.reject(new Error('Pago rechazado: ' + (data?.status_detail || data?.status)))
              } catch (e) {
                setError(e.message || String(e))
                return Promise.reject(e)
              } finally {
                setSubmitting(false)
              }
            },
            onError: (err) => {
              console.error('Brick error:', err)
              setError(err?.message || 'Error en el formulario de pago')
            },
          },
        }
        const controller = await bricksBuilder.create('payment', 'mp-pago-pedido-brick', settings)
        if (cancel) { controller.unmount?.(); return }
        brickControllerRef.current = controller
      } catch (e) {
        if (!cancel) setError('No se pudo cargar el formulario: ' + e.message)
      }
    })()
    return () => {
      cancel = true
      if (brickControllerRef.current) {
        brickControllerRef.current.unmount?.()
        brickControllerRef.current = null
      }
    }
  }, [pedido?.id, exito])

  if (loading) {
    return <Layout><main className="pp-main"><div className="pp-card">Cargando…</div></main><PpStyles /></Layout>
  }

  if (exito) {
    const aprobado = exito.status === 'approved'
    return (
      <Layout>
        <main className="pp-main">
          <div className="pp-card" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 64, marginBottom: 12 }}>{aprobado ? '✅' : '⏳'}</div>
            <h2 style={{ color: '#1a3461', margin: '0 0 8px' }}>
              {aprobado ? '¡Pago aprobado!' : 'Pago en proceso'}
            </h2>
            <p style={{ color: '#374151', margin: '0 0 20px' }}>
              {aprobado
                ? 'Tu pedido quedó pago. Te avisamos por WhatsApp cuando salga el repartidor.'
                : 'Tu pago está esperando confirmación. Te avisamos cuando se acredite.'}
            </p>
            {exito.ticket_url && (
              <a href={exito.ticket_url} target="_blank" rel="noopener"
                style={{ display: 'inline-block', marginBottom: 16, padding: '10px 20px', background: '#fef3c7', color: '#78350f', border: '1px solid #f59e0b', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>
                📄 Ver / imprimir cupón de pago
              </a>
            )}
            <div>
              <button onClick={() => navigate('/cliente')}
                style={{ padding: '13px 24px', background: '#1a3461', color: '#fff', border: 0, borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                Volver al panel →
              </button>
            </div>
          </div>
        </main>
        <PpStyles />
      </Layout>
    )
  }

  return (
    <Layout>
      <main className="pp-main">
        <div className="pp-card">
          <div className="pp-header">
            <h2>💳 Pagar tu pedido</h2>
            <div className="pp-sub">
              Pedido <strong>{pedido?.numero_orden}</strong> · ${Number(pedido?.monto_total || 0).toLocaleString('es-UY')}
            </div>
          </div>

          <div className="pp-secure">
            <span style={{ fontSize: 22 }}>🔒</span>
            <div>
              <strong>Procesado 100% por Mercado Pago</strong>
              <div style={{ fontSize: 12, marginTop: 2 }}>Elegí tu método: tarjeta, Abitab, RedPagos, MP wallet, transferencia.</div>
            </div>
          </div>

          {/* Datos del cliente que MP usa */}
          {(() => {
            const nombre = (profile?.nombre || '').trim()
            const email = user?.email || profile?.email || ''
            const phone = (profile?.phone || '').trim()
            const nombreOk = nombre.length > 1
            const emailOk = !!email
            const phoneOk = phone.replace(/\D/g, '').length >= 8
            const todoOk = nombreOk && emailOk && phoneOk
            if (todoOk) return null
            return (
              <div style={{
                marginTop: 4, marginBottom: 16, padding: 14,
                background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#78350f', marginBottom: 8 }}>
                  ⚠️ Te faltan datos antes de pagar
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                  {!nombreOk && <li>✗ Falta tu nombre</li>}
                  {!emailOk && <li>✗ Falta tu email</li>}
                  {!phoneOk && <li>✗ Falta tu teléfono</li>}
                </ul>
                <div style={{ marginTop: 10 }}>
                  <Link to="/cliente/perfil"
                    style={{
                      display: 'inline-block', padding: '8px 16px',
                      background: '#1a3461', color: '#fff',
                      borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none',
                    }}>
                    Completar mi perfil →
                  </Link>
                </div>
              </div>
            )
          })()}

          {error && <div className="pp-err">{error}</div>}

          <div id="mp-pago-pedido-brick" style={{ minHeight: brickReady ? 'auto' : 200 }}></div>

          {!brickReady && !error && (
            <div style={{ textAlign: 'center', padding: 20, color: '#6b7280', fontSize: 13 }}>
              Cargando opciones de pago…
            </div>
          )}

          {submitting && (
            <div style={{ textAlign: 'center', padding: 12, color: '#1a3461', fontWeight: 600 }}>
              Procesando pago…
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="button" onClick={() => navigate('/cliente')} disabled={submitting}
              style={{ padding: '13px 18px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              ← Pagar después
            </button>
          </div>
        </div>
      </main>
      <PpStyles />
    </Layout>
  )
}

function PpStyles() {
  return (
    <style>{`
      .pp-main { max-width: 600px; width: 100%; margin: 0 auto; padding: 28px 16px 60px; flex: 1; box-sizing: border-box; }
      .pp-card { background: #fff; border-radius: 20px; box-shadow: 0 8px 40px rgba(26,52,97,.08); border: 1px solid rgba(26,52,97,.05); padding: 24px; }
      @media (min-width: 640px) { .pp-card { padding: 32px } }
      .pp-header { padding-bottom: 16px; border-bottom: 1px solid #eef0f4; margin-bottom: 18px; }
      .pp-header h2 { margin: 0 0 6px; color: #1a3461; font-size: 22px; }
      .pp-sub { color: #6b7280; font-size: 13px; line-height: 1.4; }
      .pp-secure { display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 10px; color: #065f46; font-size: 13px; line-height: 1.4; margin-bottom: 18px; }
      .pp-err { padding: 10px 12px; background: #fee2e2; border: 1px solid #fca5a5; border-radius: 8px; color: #991b1b; font-size: 13px; margin-bottom: 12px; }
    `}</style>
  )
}
